import { createModel } from "@rematch/core";
import { bridgeAuthentication, seamlessAuthentication, getUser } from "../service/UserService";
// @ts-ignore - cwb-react might not have TypeScript definitions
import { getCurUser, setUserInfo } from "cwb-react";
// @ts-ignore - cwb-react might not have TypeScript definitions
import { getSessionDataFromAuthToken } from "cwb-react";
import * as Sentry from "@sentry/react";
import { stripBearerPrefix } from "../helpers";

interface AuthState {
  isAuthenticated: boolean;
  forbiddenError: boolean;
  createCurrentBridgeLoading: boolean;
  createSeamlessApiLoading: boolean;
  user: any | null;
  sessionData: any | null;
}

interface User {
  id?: { userId?: string };
  userId?: string;
}

interface TokenData {
  UserId?: string;
  userId?: string;
}

interface AuthResponse {
  response?: {
    status?: number;
  };
  status?: number;
}

const authModel = createModel({
  state: {
    isAuthenticated: false,
    forbiddenError: false,
    createCurrentBridgeLoading: true,
    createSeamlessApiLoading: true,
    user: null,
    sessionData: null,
  } as AuthState,
  
  reducers: {
    setIsAuthenticated: (state: AuthState, isAuthenticated: boolean): AuthState => ({
      ...state,
      isAuthenticated,
    }),
    setForbiddenError: (state: AuthState, forbiddenError: boolean): AuthState => ({
      ...state,
      forbiddenError,
    }),
    setCreateCurrentBridgeLoading: (state: AuthState, loading: boolean): AuthState => ({
      ...state,
      createCurrentBridgeLoading: loading,
    }),
    setCreateSeamlessApiLoading: (state: AuthState, loading: boolean): AuthState => ({
      ...state,
      createSeamlessApiLoading: loading,
    }),
    setUser: (state: AuthState, user: any): AuthState => ({
      ...state,
      user,
    }),
    setSessionData: (state: AuthState, sessionData: any): AuthState => ({
      ...state,
      sessionData,
    }),
    reset: (state: AuthState): AuthState => ({
      ...state,
      isAuthenticated: false,
      forbiddenError: false,
      createCurrentBridgeLoading: false,
      createSeamlessApiLoading: false,
      user: null,
      sessionData: null,
    }),
  },
  
  effects: (dispatch: any) => ({
    async checkCurrentUser(): Promise<any> {
      try {
        const user: User = getCurUser();
        // console.log('Checking current user:', user);
        
        if (user && (user.id || user.userId)) {
          dispatch.authModel.setUser(user);
          dispatch.authModel.setIsAuthenticated(true);
          
          // Also update app model for compatibility
          dispatch.appModel.setAuthenticatedUser(user);
          
          // Note: getMultipleProfiles will be called from ProtectedRoute after auth is complete
          dispatch.authModel.setCreateCurrentBridgeLoading(false);
          dispatch.authModel.setCreateSeamlessApiLoading(false);
          return user;
        }
        dispatch.authModel.setCreateCurrentBridgeLoading(false);
        dispatch.authModel.setCreateSeamlessApiLoading(false);
        return null;
      } catch (error) {
        console.error('Error checking current user:', error);
        dispatch.authModel.setCreateCurrentBridgeLoading(false);
        dispatch.authModel.setCreateSeamlessApiLoading(false);
        return null;
      }
    },
    
    async createCurrentBridge(kToken: string): Promise<any> {
      // console.log('createCurrentBridge called with token:', kToken);
      dispatch.authModel.setCreateCurrentBridgeLoading(true);
      dispatch.authModel.setForbiddenError(false);
      
      try {
        setUserInfo(null);
        const authToken: any = await bridgeAuthentication(kToken);
        let user: User
        if (authToken) {
          const tokenData: TokenData = getSessionDataFromAuthToken(stripBearerPrefix(authToken));
          const tokenUserId = tokenData.UserId || tokenData.userId;
          // bridgeAuthentication already fetched and set user data, so just get it from cache
          user = getCurUser();

          dispatch.authModel.setUser(user);
          dispatch.authModel.setSessionData(tokenData);
          dispatch.authModel.setIsAuthenticated(true);
          
          dispatch.appModel.setAuthenticatedUser(user);
          dispatch.appModel.setSessionData(tokenData);
          
          try {
            const clientId = user.id?.userId || user.userId;
          } catch (profileError) {
            console.error('Error loading multiple profiles:', profileError);
          }
          
          // console.log('Authentication successful, setting state');
          return {authToken, user};
        } else {
          // console.log('No auth token received');
          dispatch.authModel.setIsAuthenticated(false);
          return null;
        }
      } catch (error) {
        console.error("Bridge authentication failed:", error);
        Sentry.captureException(error);
        
        const authError = error as AuthResponse;
        if (authError.response?.status === 403 || authError.status === 403) {
          dispatch.authModel.setForbiddenError(true);
        }
        
        dispatch.authModel.setIsAuthenticated(false);
        return null;
      } finally {
        dispatch.authModel.setCreateCurrentBridgeLoading(false);
        dispatch.authModel.setCreateSeamlessApiLoading(false);
      }
    },

    async createSeamlessApi(): Promise<any> {
      dispatch.authModel.setCreateSeamlessApiLoading(true);
      dispatch.authModel.setForbiddenError(false);
      
      try {
        const authToken = await seamlessAuthentication();
        // console.log(authToken, 'here token');
        if (authToken) {
          const tokenData: TokenData = getSessionDataFromAuthToken(stripBearerPrefix(authToken));
          // Always fetch user data from server with the auth token to ensure consistency
          const payload = await getUser(authToken);
          let user: User;
          if (payload && payload.data) {
            user = payload.data;
            setUserInfo(user);
          } else {
            user = getCurUser();
          }

          dispatch.authModel.setUser(user);
          dispatch.authModel.setSessionData(tokenData);
          dispatch.authModel.setIsAuthenticated(true);
          
          dispatch.appModel.setAuthenticatedUser(user);
          dispatch.appModel.setSessionData(tokenData);
          
          return {authToken, user};
        } else {
          dispatch.authModel.setIsAuthenticated(false);
          return null;
        }
      } catch (error) {
        console.error("Seamless authentication failed:", error);
        Sentry.captureException(error);
        
        const authError = error as AuthResponse;
        if (authError.response?.status === 403 || authError.status === 403) {
          dispatch.authModel.setForbiddenError(true);
        }
        
        dispatch.authModel.setIsAuthenticated(false);
        return null;
      } finally {
        debugger;
        dispatch.authModel.setCreateSeamlessApiLoading(false);
      }
    },
  }),
});

export default authModel;